// Banned — a factual, running record of communities that have banned this
// account (u/DignityAHP) for replying to people asking how to find cheaper
// care with prices linked from hospitals' own published files.
//
// This is not a complaint page. Each entry pairs the exact reply this account
// posted (or the ban notice itself, when no reply preceded it) with the
// ban — real evidence, not an accusation about any specific moderator's
// reasoning. Grows as it happens; nothing here is backfilled or invented.

import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { AlertOctagon, ExternalLink, ShieldOff } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

function useThemeTokens() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: "#0a1628", bgAlt: "#050e1a",
        text: "#FAF9F6", textMuted: "rgba(250,249,246,0.62)",
        gold: "#C5A55A", red: "#B22234", navy: "#5B9BD5",
        cardBg: "#0d1e35", cardBorder: "rgba(250,249,246,0.10)",
        divider: "rgba(250,249,246,0.10)", chipBg: "rgba(178,34,52,0.14)",
        quoteBg: "rgba(250,249,246,0.05)",
      };
    }
    return {
      bg: "#FAF9F6", bgAlt: "#ffffff",
      text: "#1B1B1B", textMuted: "rgba(27,27,27,0.62)",
      gold: "#C5A55A", red: "#B22234", navy: "#002868",
      cardBg: "#ffffff", cardBorder: "rgba(27,27,27,0.08)",
      divider: "rgba(27,27,27,0.08)", chipBg: "rgba(178,34,52,0.07)",
      quoteBg: "rgba(27,27,27,0.03)",
    };
  }, [isDark]);
}

interface BanEntry {
  subreddit: string;
  subredditUrl: string;
  dateLabel: string;
  threadTitle?: string;
  threadUrl?: string;
  reply?: string;
  replyLinks?: { label: string; to: string }[];
  note: string;
}

// Real entries only — add one the day it happens, in the exact words of what
// was posted (or the ban notice, if nothing was posted first). Never backfill
// a reason we don't actually know.
const BAN_LOG: BanEntry[] = [
  {
    subreddit: "r/Insurance",
    subredditUrl: "https://www.reddit.com/r/Insurance/",
    dateLabel: "September 4, 2026",
    threadTitle: "Insurance/Doctor Office Miscommunication",
    threadUrl: "https://www.reddit.com/r/Insurance/comments/1w6gjw7/insurancedoctor_office_miscommunication/",
    reply: `Two things that may cut through the back-and-forth.

First, a denial of the inpatient stay is not a denial of the surgery. Your read is the right one: insurers routinely approve a single-level fusion as outpatient or observation and deny the inpatient admission, and the surgeon's office can simply schedule it that way. If a complication turns it into an admission, that gets reviewed on its own. Ask the office to resubmit as outpatient with observation rather than waiting on an inpatient appeal.

Second, since surgery is scheduled, you're entitled to an expedited (urgent) appeal, which most plans have to decide within 72 hours, and the missed peer-to-peer window doesn't block it. Call the insurer and use the words "expedited appeal, scheduled surgery on September 15." Get the reference number. Then tell the office you've opened it and they need to send the clinical notes today.

For scale, since hospitals have had to publish their prices since 2021: the facility line for a single-level posterior lumbar fusion (CPT 22612) has a median list price of $13,866 across 912 hospitals that publish it, with the priciest tenth over $95,927, and the whole fusion episode lists at a median of about $80,800. The inpatient room and board is a small fraction of that, which is why insurers push observation; it's also why you want the surgery itself approved in writing before the 15th, whichever status it ends up under.

Disclosure: this is the American Hospital Prices account. The fusion line by hospital, free, no account needed: americanhospitalprices.com/cpt/22612`,
    replyLinks: [{ label: "americanhospitalprices.com/cpt/22612", to: "/cpt/22612" }],
    note: "Posted the reply above. The permanent ban notice arrived the same day.",
  },
  {
    subreddit: "r/HealthInsurance",
    subredditUrl: "https://www.reddit.com/r/HealthInsurance/",
    dateLabel: "September 2, 2026",
    note: "No reply from this account preceded this one — the ban was already in place when we went to draft a reply here, discovered only when the community's own chat system confirmed it. We don't know what triggered it, and we're not going to guess. Listed because it's real and it's the same account.",
  },
];

const Banned = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const T = useThemeTokens();
  const pageTitle = `Banned for Sharing Prices | ${brand.name}`;
  const pageDescription =
    "A factual record of the online communities that have banned this account for replying to people asking how to find cheaper care with prices from hospitals' own published files.";

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: sans, paddingBottom: 96 }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`${brand.primaryDomain}/banned`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/banned`} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 900, paddingTop: 96, paddingBottom: 64 }}>
          <div
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-6"
            style={{ color: T.red, fontWeight: 600 }}
          >
            <ShieldOff className="w-3.5 h-3.5" />
            A running record
          </div>
          <h1
            style={{
              fontFamily: serif,
              fontSize: "clamp(2.2rem, 4.8vw, 3.8rem)",
              lineHeight: 1.06,
              letterSpacing: "-0.02em",
              color: T.text,
              margin: 0,
              maxWidth: 780,
            }}
          >
            Banned for sharing real prices.
          </h1>
          <p style={{ color: T.textMuted, fontSize: "1.1rem", lineHeight: 1.6, marginTop: 24, maxWidth: 680 }}>
            This account (u/DignityAHP) answers people asking how much a
            procedure actually costs, then links the exact hospital price
            page that backs the number. Below is every community that has
            permanently banned the account for it — the reply we posted, and
            the ban that followed. Nothing added, nothing guessed at.
          </p>
          <p style={{ color: T.textMuted, fontSize: "0.9rem", lineHeight: 1.6, marginTop: 18, maxWidth: 680 }}>
            This isn't a complaint about any specific moderator's reasoning —
            most bans arrive with no stated reason at all, and we're not going
            to invent one. It's just the record, kept because it's relevant:
            if answering "what will this actually cost me" with a hospital's
            own published number gets an account banned, that says something
            about who these spaces are actually built to help.
          </p>
        </div>
      </section>

      {/* ── Log ─────────────────────────────────────────────────────────── */}
      <section className="mx-auto px-6" style={{ maxWidth: 900, paddingTop: 56 }}>
        <div className="flex flex-col" style={{ gap: 32 }}>
          {BAN_LOG.map((entry) => (
            <article
              key={entry.subreddit}
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderLeft: `4px solid ${T.red}`,
                borderRadius: 8,
                padding: "28px 32px",
              }}
            >
              <div className="flex items-start justify-between flex-wrap gap-3" style={{ marginBottom: 6 }}>
                <div className="flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4" style={{ color: T.red }} />
                  <a
                    href={entry.subredditUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontFamily: serif, fontSize: "1.5rem", color: T.text, textDecoration: "none" }}
                  >
                    {entry.subreddit}
                  </a>
                </div>
                <span
                  className="text-xs uppercase tracking-wider"
                  style={{ color: T.red, background: T.chipBg, padding: "4px 10px", borderRadius: 999, fontWeight: 600 }}
                >
                  Permanently banned · {entry.dateLabel}
                </span>
              </div>

              {entry.threadTitle && entry.threadUrl && (
                <p style={{ color: T.textMuted, fontSize: "0.92rem", marginBottom: 16 }}>
                  Replying to:{" "}
                  <a
                    href={entry.threadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: T.navy, textDecoration: "underline", textUnderlineOffset: 2 }}
                  >
                    {entry.threadTitle}
                    <ExternalLink className="inline w-3 h-3 ml-1 -mt-0.5" />
                  </a>
                </p>
              )}

              {entry.reply && (
                <div
                  style={{
                    background: T.quoteBg,
                    border: `1px solid ${T.divider}`,
                    borderRadius: 6,
                    padding: "18px 20px",
                    fontSize: "0.92rem",
                    lineHeight: 1.7,
                    color: T.text,
                    whiteSpace: "pre-wrap",
                    marginBottom: 14,
                  }}
                >
                  {entry.reply}
                </div>
              )}

              {entry.replyLinks && entry.replyLinks.length > 0 && (
                <div className="flex flex-wrap gap-2" style={{ marginBottom: 14 }}>
                  {entry.replyLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="text-sm"
                      style={{ color: T.navy, textDecoration: "underline", textUnderlineOffset: 2 }}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}

              <p style={{ color: T.textMuted, fontSize: "0.88rem", lineHeight: 1.6, margin: 0 }}>
                {entry.note}
              </p>
            </article>
          ))}
        </div>

        <p style={{ color: T.textMuted, fontSize: "0.85rem", marginTop: 48, maxWidth: 680, lineHeight: 1.6 }}>
          This list only grows — entries are added the day a ban happens, in
          the exact wording of what was posted. If you've run into the same
          thing sharing a hospital's own published price somewhere, that's
          worth knowing about too.
        </p>
      </section>
    </div>
  );
};

export default Banned;
